import { useEffect, useRef, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { CouponRow, PriceRow } from 'server/types';
import { HtmlTemplateConfig } from 'server/types/pmts-overrides';
import { PurchaseFn, StripeHandlerFn } from 'shared/payments/types';
import { CheckoutFormWizard } from './checkout-form-wizard';
import { useDocumentTitle } from 'client/utils/use-document-title';

type Props = {
  config: HtmlTemplateConfig;
  course: { id: string; title: string };
  price: PriceRow;
  coupon?: CouponRow;
  collectAddress: boolean;
  handleStripe?: StripeHandlerFn;
  beginPurchase: PurchaseFn;
  onPurchaseComplete(opts?: { newUser?: boolean }): Promise<unknown>;
};

const FORM_ISOLATION_CSS = `
#checkout-form {
  font-family: 'Montserrat', system-ui, -apple-system, sans-serif;
  font-size: 16px;
  line-height: 1.5;
  color: #1f2937;
  box-sizing: border-box;
}
#checkout-form *, #checkout-form *::before, #checkout-form *::after {
  box-sizing: border-box;
}
#checkout-form input, #checkout-form select, #checkout-form button {
  font-family: inherit;
}
`;

export function HtmlTemplateCheckoutPage({
  config,
  course,
  price,
  coupon,
  collectAddress,
  handleStripe,
  beginPurchase,
  onPurchaseComplete,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | undefined>(undefined);

  useDocumentTitle([course.title, 'Checkout']);

  useEffect(() => {
    setPortalTarget(undefined);
    if (containerRef.current) {
      const slot = containerRef.current.querySelector('#checkout-form');
      if (slot instanceof HTMLElement) {
        setPortalTarget(slot);
      }
    }
  }, [config.html]);

  return (
    <div>
      <style dangerouslySetInnerHTML={{ __html: FORM_ISOLATION_CSS }} />
      <div ref={containerRef} dangerouslySetInnerHTML={{ __html: config.html }} />
      {portalTarget &&
        createPortal(
          <CheckoutFormWizard
            courseId={course.id}
            price={price}
            coupon={coupon}
            collectAddress={collectAddress}
            handleStripe={handleStripe}
            beginPurchase={beginPurchase}
            onPurchaseComplete={onPurchaseComplete}
          />,
          portalTarget,
        )}
    </div>
  );
}
